<script>
import { computed, ref, watch } from 'vue'
import { authService } from '@/services/api'

export default {
  name: 'RegisterationModal',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    profile: {
      type: Object,
      default: null,
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'completed'],
  setup(props, { emit }) {
    const isOpen = computed(() => props.modelValue)
    const registrationLoading = ref(false)
    const registrationStatus = ref({ type: '', message: '' })
    const registrationData = ref({
      name: '',
      phone: '',
      national_code: '',
      address: '',
      postal_code: '',
      mohal_kar: '',
    })

    const resetRegistration = () => {
      registrationData.value = {
        name: '',
        phone: '',
        national_code: '',
        address: '',
        postal_code: '',
        mohal_kar: '',
      }
      registrationStatus.value = { type: '', message: '' }
    }

    const loadRegistrationData = () => {
      resetRegistration()
      let user = {}

      try {
        const storedUser = JSON.parse(window.localStorage.getItem('user') || '{}')
        user = storedUser && typeof storedUser === 'object' ? storedUser : {}
      } catch {
        user = {}
      }

      registrationData.value.name = [props.profile?.first_name, props.profile?.last_name].filter(Boolean).join(' ')
        || user.name
        || [user.first_name, user.last_name].filter(Boolean).join(' ')
      registrationData.value.phone = props.profile?.phone || user.phone || user.phone_number || ''
      registrationData.value.national_code = props.profile?.national_code || ''
      registrationData.value.address = props.profile?.address || ''
      registrationData.value.postal_code = props.profile?.postal_code || ''
      registrationData.value.mohal_kar = props.profile?.mohal_kar || ''
    }

    watch(() => props.modelValue, (visible) => {
      if (visible) loadRegistrationData()
      else resetRegistration()
    }, { immediate: true })

    watch(() => props.profile, (profile) => {
      if (props.modelValue && profile) loadRegistrationData()
    }, { deep: true })

    const closeModal = () => {
      emit('update:modelValue', false)
    }

    const submitRegistration = async () => {
      if (registrationLoading.value) return

      const toEnglishDigits = (value) => String(value || '')
        .replace(/[۰-۹]/g, digit => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
        .replace(/[٠-٩]/g, digit => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
      registrationData.value.name = String(registrationData.value.name || '').trim()
      registrationData.value.phone = String(registrationData.value.phone || '').trim()
      registrationData.value.national_code = toEnglishDigits(registrationData.value.national_code).replace(/\D/g, '')
      registrationData.value.postal_code = toEnglishDigits(registrationData.value.postal_code).replace(/\D/g, '')
      registrationData.value.address = String(registrationData.value.address || '').trim()
      registrationData.value.mohal_kar = String(registrationData.value.mohal_kar || '').trim()
      if (!registrationData.value.name || !registrationData.value.phone) {
        registrationStatus.value = { type: 'error', message: 'نام و شماره تلفن الزامی است.' }
        return
      }
      if (!/^\d{10}$/.test(registrationData.value.national_code)) {
        registrationStatus.value = { type: 'error', message: 'کد ملی باید دقیقاً ۱۰ رقم باشد.' }
        return
      }
      if (registrationData.value.postal_code && !/^\d{10}$/.test(registrationData.value.postal_code)) {
        registrationStatus.value = { type: 'error', message: 'کد پستی باید دقیقاً ۱۰ رقم باشد.' }
        return
      }

      registrationLoading.value = true
      registrationStatus.value = { type: '', message: '' }

      try {
        const payload = {
          ...registrationData.value,
          name: registrationData.value.name,
          phone: registrationData.value.phone,
          address: registrationData.value.address || '',
          postal_code: registrationData.value.postal_code || '',
          mohal_kar: registrationData.value.mohal_kar || '',
        }
        const response = await authService.completeCustomerRegistration(payload)

        if (!response.data.success) {
          throw new Error(response.data.message || 'Registration failed.')
        }

        registrationStatus.value = {
          type: 'success',
          message: response.data.message || 'Registration completed successfully.',
        }
        emit('completed', { ...response.data, registration: payload })
        emit('update:modelValue', false)
      } catch (error) {
        registrationStatus.value = {
          type: 'error',
          message: error.response?.data?.detail
            || error.response?.data?.message
            || error.message
            || 'Registration failed.',
        }
      } finally {
        registrationLoading.value = false
      }
    }

    return {
      closeModal,
      isOpen,
      registrationData,
      registrationLoading,
      registrationStatus,
      submitRegistration,
    }
  },
}
</script>
<template>
  <Teleport to="body">
    <div v-if="isOpen" class="registration-modal-overlay" @click.self="!required && closeModal()">
      <div
        class="registration-modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="registration-modal-title"
        @click.stop
      >
        <div class="registration-modal-header">
          <h3 id="registration-modal-title">📝 تکمیل ثبت نام</h3>
          <button
            v-if="!required"
            type="button"
            class="close-modal-btn"
            aria-label="Close registration form"
            @click="closeModal"
          >
            ×
          </button>
        </div>

        <div class="registration-modal-body">
          <div class="registration-message">
            <p>⚠️ اطلاعات شما ناقص است!</p>
            <p>لطفاً اطلاعات زیر را برای تکمیل ثبت نام وارد کنید:</p>
          </div>

          <form class="registration-form" @submit.prevent="submitRegistration">
            <div class="form-group">
              <label for="customerName">نام و نام خانوادگی *</label>
              <input
                id="customerName"
                v-model="registrationData.name"
                type="text"
                placeholder="مثال: علی محمدی"
                required
                class="form-input"
              />
            </div>

            <div class="form-group">
              <label for="customerPhone">شماره تلفن *</label>
              <input
                id="customerPhone"
                v-model="registrationData.phone"
                type="tel"
                placeholder="مثال: 09123456789"
                required
                class="form-input"
                :readonly="Boolean(registrationData.phone)"
              />
              <small v-if="registrationData.phone" class="form-hint">
                شماره تلفن شما به صورت خودکار از حساب کاربری گرفته شده است
              </small>
            </div>

            <div class="form-group">
              <label for="customerNationalCode">کد ملی *</label>
              <input
                id="customerNationalCode"
                v-model="registrationData.national_code"
                type="text"
                placeholder="مثال: 1234567890"
                required
                class="form-input"
              />
            </div>

            <div class="form-group">
              <label for="customerMohalKar">نام محل کار</label>
              <input
                id="customerMohalKar"
                v-model="registrationData.mohal_kar"
                type="text"
                placeholder="محل کار خود را وارد کنید"
                class="form-input"
              />
            </div>

            <div class="form-group">
              <label for="customerAddress">آدرس محل کار</label>
              <textarea
                id="customerAddress"
                v-model="registrationData.address"
                placeholder="آدرس کامل خود را وارد کنید"
                class="form-textarea"
                rows="3"
              ></textarea>
            </div>

            <div class="form-group">
              <label for="customerPostalCode">کد پستی محل کار</label>
              <input
                id="customerPostalCode"
                v-model="registrationData.postal_code"
                type="text"
                placeholder="مثال: 1234567890"
                class="form-input"
              />
            </div>

            <p
              v-if="registrationStatus.message"
              class="registration-status"
              :class="registrationStatus.type"
              role="status"
              aria-live="polite"
            >
              {{ registrationStatus.message }}
            </p>

            <div class="form-actions">
              <button v-if="!required" type="button" class="cancel-btn" @click="closeModal">انصراف</button>
              <button type="submit" class="register-btn" :disabled="registrationLoading">
                {{ registrationLoading ? 'در حال ثبت...' : 'ثبت نام' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </Teleport>
</template>
<style>
@import url('https://fonts.googleapis.com/css2?family=Vazirmatn:wght@400;500;600;700;800&display=swap');

/*
 * The modal is teleported to <body>, so it cannot inherit the scoped theme
 * variables from the auth page that opened it. Keep the auth palette local
 * to the modal and switch it from the same document-level theme attribute.
 */
.registration-modal-overlay {
  --background-fallback: #0a1c30;
  --page-background: linear-gradient(135deg, #06151e 0%, #08242e 52%, #071b25 100%);
  --card-background: rgba(12, 42, 51, 0.92);
  --control-background: rgba(5, 24, 31, 0.72);
  --control-background-muted: rgba(5, 24, 31, 0.56);
  --control-border: rgba(145, 205, 194, 0.25);
  --text-primary: #f1f7f5;
  --text-secondary: #d7e6e2;
  --text-muted: #9bb5b4;
  --border: rgba(145, 205, 194, 0.2);
  --glass-border: rgba(145, 205, 194, 0.22);
  --accent: #f07832;
  --accent-soft: #ff9b5f;
  --accent-border: rgba(240, 120, 50, 0.34);
  --chart-glow: rgba(240, 120, 50, 0.2);
  --modal-overlay: rgba(2, 12, 19, 0.72);
  --ghost-border: rgba(145, 205, 194, 0.2);
  --ghost-background: rgba(145, 205, 194, 0.08);
  --glass-shadow: rgba(0, 0, 0, 0.42);
  --heading-text: #f1f7f5;
  --soft-text: #9bb5b4;
}

html[data-theme='light'] .registration-modal-overlay {
  --background-fallback: #575757;
  --page-background: linear-gradient(180deg, #414141 0%, #616161 100%);
  --card-background: rgba(55, 55, 55, 0.94);
  --control-background: rgba(55, 55, 55, 0.55);
  --control-background-muted: rgba(55, 55, 55, 0.45);
  --control-border: rgba(222, 222, 222, 0.2);
  --text-primary: #f3f3f3;
  --text-secondary: #dcdcdc;
  --text-muted: #b7b7b7;
  --border: rgba(228, 228, 228, 0.18);
  --glass-border: rgba(238, 238, 238, 0.16);
  --accent: #f07b32;
  --accent-soft: #f39a61;
  --accent-border: rgba(240, 123, 50, 0.42);
  --chart-glow: rgba(240, 123, 50, 0.2);
  --modal-overlay: rgba(20, 20, 20, 0.64);
  --ghost-border: rgba(228, 228, 228, 0.18);
  --ghost-background: rgba(228, 228, 228, 0.08);
  --glass-shadow: rgba(26, 26, 26, 0.42);
  --heading-text: #f3f3f3;
  --soft-text: #b7b7b7;
}

.registration-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: var(--modal-overlay);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.registration-modal-content,
.registration-modal-content * {
  box-sizing: border-box;
}

.registration-modal-content {
  width: min(560px, 100%);
  max-height: min(90vh, 820px);
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--accent) transparent;
  border: 1px solid var(--glass-border);
  border-radius: 22px;
  background: var(--card-background);
  color: var(--text-primary);
  box-shadow: 0 24px 70px var(--glass-shadow), inset 0 1px 0 rgba(255, 255, 255, 0.04);
  animation: registration-modal-in 0.22s ease-out;
  direction: rtl;
  font-family: 'Vazirmatn', Tahoma, 'Segoe UI', sans-serif;
}

.registration-modal-content::-webkit-scrollbar {
  width: 6px;
}

.registration-modal-content::-webkit-scrollbar-track {
  background: transparent;
}

.registration-modal-content::-webkit-scrollbar-thumb {
  border: 1px solid var(--card-background);
  border-radius: 999px;
  background: var(--accent);
}

.registration-modal-content::-webkit-scrollbar-thumb:hover {
  background: var(--accent-soft);
}

.registration-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--ghost-border);
  background: color-mix(in srgb, var(--card-background) 82%, transparent);
}

.registration-modal-header h3 {
  margin: 0;
  color: var(--heading-text);
  font-size: 1.1rem;
  font-weight: 800;
}

.close-modal-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--soft-text);
  font-size: 25px;
  cursor: pointer;
  transition: color 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
}

.close-modal-btn:hover {
  transform: rotate(90deg);
  background: var(--ghost-background);
  color: var(--accent);
}

.registration-modal-body {
  padding: 24px;
}

.registration-message {
  margin-bottom: 18px;
  padding: 13px 15px;
  border: 1px solid var(--accent-border);
  border-radius: 12px;
  background: color-mix(in srgb, var(--accent) 10%, var(--card-background));
  color: var(--text-primary);
  line-height: 1.7;
}

.registration-message p {
  margin: 3px 0;
}

.registration-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.registration-form .form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.registration-form .form-group label {
  color: var(--text-primary);
  font-size: 0.88rem;
  font-weight: 600;
}

.registration-form .form-input,
.registration-form .form-textarea {
  width: 100%;
  min-height: 46px;
  padding: 11px 14px;
  border: 1px solid var(--ghost-border);
  border-radius: 10px;
  background: var(--control-background-muted) !important;
  color: var(--text-primary);
  font: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.registration-form .form-input:focus,
.registration-form .form-textarea:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-border);
}

.registration-form .form-input::placeholder,
.registration-form .form-textarea::placeholder {
  color: var(--text-muted);
  opacity: 0.9;
}

.registration-form .form-input:-webkit-autofill,
.registration-form .form-input:-webkit-autofill:hover,
.registration-form .form-input:-webkit-autofill:focus {
  -webkit-text-fill-color: var(--text-primary);
  -webkit-box-shadow: 0 0 0 1000px var(--control-background-muted) inset;
  box-shadow: 0 0 0 1000px var(--control-background-muted) inset;
  caret-color: var(--text-primary);
}

.registration-form .form-input[readonly] {
  opacity: 0.75;
  cursor: not-allowed;
}

.registration-form .form-textarea {
  resize: vertical;
}

.registration-form .form-hint {
  color: var(--soft-text);
  font-size: 0.76rem;
}

.registration-status {
  margin: 0;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 0.88rem;
  line-height: 1.6;
}

.registration-status.success {
  background: rgba(16, 185, 129, 0.14);
  color: #34d399;
}

.registration-status.error {
  background: rgba(239, 68, 68, 0.14);
  color: #f87171;
}

.registration-form .form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
}

.registration-form .cancel-btn,
.registration-form .register-btn {
  min-height: 46px;
  padding: 10px 20px;
  border-radius: 10px;
  font: inherit;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.18s ease, opacity 0.18s ease, background-color 0.18s ease;
}

.registration-form .cancel-btn {
  border: 1px solid var(--ghost-border);
  background: var(--ghost-background);
  color: var(--text-primary);
}

.registration-form .register-btn {
  border: 0;
  background: var(--accent);
  color: var(--text-primary);
  box-shadow: 0 8px 20px var(--chart-glow);
}

.registration-form .register-btn:hover:not(:disabled) {
  background: var(--accent-soft);
  box-shadow: 0 10px 26px var(--chart-glow);
}

.registration-form .cancel-btn:hover {
  border-color: var(--accent-border);
  background: var(--ghost-background);
  color: var(--accent-soft);
}

.registration-form .cancel-btn:hover,
.registration-form .register-btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.registration-form .register-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@keyframes registration-modal-in {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (max-width: 480px) {
  .registration-modal-content {
    max-height: 94vh;
    border-radius: 18px;
  }

  .registration-modal-header,
  .registration-modal-body {
    padding: 18px;
  }

  .registration-form .form-actions {
    flex-direction: column-reverse;
  }

  .registration-form .cancel-btn,
  .registration-form .register-btn {
    width: 100%;
  }
}
</style>
